// frontend/src/components/chat/ChatDrawer.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  InputAdornment,
  Alert,
  ListItemButton,
  Chip,
  CircularProgress,
  Skeleton,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  GroupAdd as GroupAddIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { userStatusAPI, conversationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';

// ─────────────────────────────────────────────────────────────
// 🎨 Syncro Theme Constants (Shared Design System)
// ─────────────────────────────────────────────────────────────
const THEME = {
  primary: '#667eea',
  primaryLight: 'rgba(102, 126, 238, 0.12)',
  primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  secondary: '#764ba2',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textDisabled: '#94a3b8',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  background: '#f8fafc',
  surface: '#ffffff',
  hover: 'rgba(0, 0, 0, 0.04)',
  hoverPrimary: 'rgba(102, 126, 238, 0.08)',
  online: '#22c55e',
  offline: '#94a3b8',
  unreadDot: '#ff6b6b',
};

// ─────────────────────────────────────────────────────────────
// 🔧 Helper Functions
// ─────────────────────────────────────────────────────────────
const formatLastSeen = (dateStr) => {
  if (!dateStr) return 'Offline';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const getAvatarUrl = (avatar) => {
  // Defensive env variable access (works with CRA & Vite)
  let baseUrl = 'http://localhost:8000/storage';
  try {
    baseUrl = import.meta.env?.VITE_AVATAR_URL ||
      process.env?.REACT_APP_AVATAR_URL ||
      baseUrl;
  } catch (e) {
    // Fallback silently
  }
  return avatar ? `${baseUrl}/${avatar}` : null;
};

const truncateText = (text, maxLength = 35) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;
};

// ─────────────────────────────────────────────────────────────
// 🧩 Avatar Component with Online Status
// ─────────────────────────────────────────────────────────────
const StatusAvatar = ({ user, size = 40 }) => {
  const avatarUrl = getAvatarUrl(user?.avatar);
  const isOnline = user?.is_online === true || user?.status === 'online';

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Avatar
        src={avatarUrl}
        sx={{
          width: size,
          height: size,
          bgcolor: THEME.primary,
          fontSize: size * 0.4,
          border: '2px solid white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'transform 0.15s ease',
          '&:hover': { transform: 'scale(1.03)' }
        }}
      >
        {!avatarUrl && (user?.name?.charAt(0)?.toUpperCase() || '👤')}
      </Avatar>
      <Box
        sx={{
          position: 'absolute',
          bottom: 2,
          right: 2,
          width: 11,
          height: 11,
          bgcolor: isOnline ? THEME.online : THEME.offline,
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: isOnline ? `0 0 0 2px ${THEME.primaryLight}` : 'none',
          transition: 'background-color 0.2s ease'
        }}
        aria-label={isOnline ? 'Online' : 'Offline'}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// 🎯 Main Component
// ─────────────────────────────────────────────────────────────
const ChatDrawer = () => {
  const { user: authUser } = useAuth();
  const {
    conversations,
    onlineUsersData,
    loadMessages,
    open,
    setOpen,
    isLoading = false
  } = useChat();

  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('all'); // 'all' | 'people' | 'conversations'

  // ─────────────────────────────────────────────────────────
  // 🔄 Effects
  // ─────────────────────────────────────────────────────────
  // useEffect(() => {
  //   if (open) {
  //     userStatusAPI.update('online').catch(console.error);
  //   }
  //   return () => {
  //     if (!open) {
  //       userStatusAPI.update('offline').catch(console.error);
  //     }
  //   };
  // }, [open]);

  useEffect(() => {
    // User online when app loads
    userStatusAPI.update('online').catch(console.error);

    // Browser/tab close হলে offline
    const handleUnload = () => {
      navigator.sendBeacon(
        '/api/v1/users/status',
        JSON.stringify({ status: 'offline' })
      );
    };

    window.addEventListener('beforeunload', handleUnload);

    // Heartbeat every 30 sec
    const interval = setInterval(() => {
      userStatusAPI.update('online').catch(console.error);
    }, 30000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, setOpen]);

  // ─────────────────────────────────────────────────────────
  // 🧠 Memoized Data Processing
  // ─────────────────────────────────────────────────────────
  const allUsers = useMemo(() =>
    (onlineUsersData || []).filter(u => u.id !== authUser?.id),
    [onlineUsersData, authUser?.id]
  );

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return allUsers;
    const query = searchQuery.toLowerCase();
    return allUsers.filter(u =>
      u.name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.role?.toLowerCase().includes(query)
    );
  }, [allUsers, searchQuery]);

  const sortedUsers = useMemo(() =>
    [...filteredUsers].sort((a, b) => {
      const aOnline = a.is_online === true || a.status === 'online';
      const bOnline = b.is_online === true || b.status === 'online';

      if (aOnline && !bOnline) return -1;
      if (!aOnline && bOnline) return 1;
      return (a.name || '').localeCompare(b.name || '');
    }),
    [filteredUsers]
  );

  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter(c =>
      c.name?.toLowerCase().includes(query) ||
      c.other_users?.some(u => u.name?.toLowerCase().includes(query)) ||
      c.last_message?.body?.toLowerCase().includes(query)
    );
  }, [conversations, searchQuery]);

  const getUnreadCount = useCallback((conv) => {
    if (!conv?.last_message || conv.last_message.user_id === authUser?.id) return 0;
    const lastRead = conv.pivot?.last_read_at;
    const lastMsg = conv.last_message?.created_at;
    if (!lastRead || !lastMsg) return 1;
    return new Date(lastMsg) > new Date(lastRead) ? 1 : 0;
  }, [authUser?.id]);

  const totalUnread = useMemo(() =>
    conversations.reduce((sum, c) => sum + getUnreadCount(c), 0),
    [conversations, getUnreadCount]
  );

  const onlineCount = useMemo(() =>
    sortedUsers.filter(
      u => u.is_online === true || u.status === 'online'
    ).length,
    [sortedUsers]
  );

  // ─────────────────────────────────────────────────────────
  // 🎬 Handlers
  // ─────────────────────────────────────────────────────────
  const onClose = useCallback(() => setOpen(false), [setOpen]);

  const handleStartChat = useCallback(async (recipientId) => {
    try {
      setError('');
      const res = await conversationAPI.createDirect(recipientId);
      const convId = res.data?.conversation?.id || res.data?.id || res.data?.conversation;
      if (convId) {
        loadMessages(convId);
        setOpen(false);
      }
    } catch (err) {
      console.error('Chat creation error:', err);
      setError('Failed to start conversation. Please try again.');
    }
  }, [loadMessages, setOpen]);

  const handleConversationClick = useCallback((convId) => {
    loadMessages(convId);
    setOpen(false);
  }, [loadMessages, setOpen]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setError('');
  }, []);

  // ─────────────────────────────────────────────────────────
  // 🎨 Render Helpers
  // ─────────────────────────────────────────────────────────
  const renderSectionHeader = (title, count, icon, active) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        cursor: 'pointer',
        borderRadius: 2,
        bgcolor: active ? THEME.hoverPrimary : 'transparent',
        transition: 'all 0.15s ease',
        '&:hover': { bgcolor: THEME.hoverPrimary }
      }}
      onClick={() => setActiveSection(active ? 'all' : title.toLowerCase())}
    >
      {icon}
      <Typography variant="subtitle2" fontWeight={600} color={THEME.textPrimary}>
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{
            ml: 'auto',
            height: 20,
            fontSize: '0.7rem',
            bgcolor: active ? THEME.primary : THEME.borderLight,
            color: active ? 'white' : THEME.textSecondary,
            '& .MuiChip-label': { px: 1 }
          }}
        />
      )}
    </Box>
  );

  // ─────────────────────────────────────────────────────────
  // 📦 Main Render
  // ─────────────────────────────────────────────────────────
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      BackdropProps={{
        invisible: true,
        sx: { bgcolor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)' }
      }}
      sx={{
        zIndex: 1300,
        '& .MuiDrawer-paper': {
          borderLeft: `1px solid ${THEME.border}`,
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.12)',
          bgcolor: THEME.surface
        }
      }}
    >
      <Box sx={{
        width: 380,
        maxWidth: '95vw',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative'
      }}>
        {/* Gradient Accent Bar */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: THEME.primaryGradient,
          zIndex: 1
        }} />

        {/* Header */}
        <Box sx={{
          p: 2.5,
          pb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${THEME.borderLight}`,
          bgcolor: THEME.surface,
          zIndex: 2
        }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color={THEME.textPrimary}>
              Messages
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              {totalUnread > 0 ? (
                <Typography variant="caption" color={THEME.textSecondary}>
                  {totalUnread} unread
                </Typography>
              ) : (
                <Typography variant="caption" color={THEME.success} fontWeight={500}>
                  ✓ All caught up
                </Typography>
              )}
              <Typography variant="caption" color={THEME.textDisabled}>
                • {onlineCount} online
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="New Group Chat">
              <IconButton
                size="small"
                sx={{ color: THEME.textSecondary }}
                onClick={() => { }}
              >
                <GroupAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: THEME.textSecondary,
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)', color: THEME.error }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: THEME.surface, zIndex: 2 }}>
          <TextField
            size="small"
            placeholder="Search people or conversations..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: THEME.background,
                '& fieldset': { borderColor: THEME.border },
                '&:hover fieldset': { borderColor: THEME.primary },
                '&.Mui-focused fieldset': { borderColor: THEME.primary, borderWidth: 2 }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: THEME.textDisabled }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" sx={{ color: THEME.textDisabled }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mx: 2.5, mb: 1, borderRadius: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Content List */}
        <List sx={{
          flexGrow: 1,
          overflow: 'auto',
          px: 1.5,
          pb: 2,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(0,0,0,0.15)',
            borderRadius: '3px',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' }
          }
        }}>
          {isLoading ? (
            // Loading Skeletons
            [...Array(5)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                <Skeleton variant="circular" width={44} height={44} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton width="60%" height={16} sx={{ mb: 0.5 }} />
                  <Skeleton width="40%" height={12} />
                </Box>
              </Box>
            ))
          ) : (
            <>
              {/* People Section */}
              {sortedUsers.length > 0 && (
                <>
                  {renderSectionHeader(
                    'People',
                    onlineCount,
                    <PersonAddIcon fontSize="small" sx={{ color: THEME.primary }} />,
                    activeSection === 'people'
                  )}

                  {(activeSection === 'all' || activeSection === 'people') && sortedUsers.map((u) => (
                    <ListItemButton
                      key={`user-${u.id}`}
                      onClick={() => handleStartChat(u.id)}
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        borderRadius: 2,
                        my: 0.25,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: THEME.hover,
                          transform: 'translateX(3px)',
                          '& .status-dot': { transform: 'scale(1.1)' }
                        },
                        borderLeft: '3px solid transparent',
                        '&:hover': { borderLeftColor: THEME.primary }
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 56 }}>
                        <StatusAvatar user={u} />
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              color={THEME.textPrimary}
                              noWrap
                            >
                              {u.name}
                            </Typography>
                            {u.role && (
                              <Chip
                                label={u.role}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: THEME.primaryLight,
                                  color: THEME.primary,
                                  fontWeight: 500,
                                  '& .MuiChip-label': { px: 0.75 }
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              className="status-dot"
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: u.is_online ? THEME.online : THEME.offline,
                                transition: 'transform 0.15s ease'
                              }}
                            />
                            <Typography
                              variant="caption"
                              color={u.is_online ? THEME.success : THEME.textDisabled}
                              fontWeight={u.is_online ? 600 : 400}
                            >
                              {u.is_online ? 'Online' : `Last seen ${formatLastSeen(u.last_seen_at)}`}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />

                      <Tooltip title="Start chat">
                        <IconButton
                          size="small"
                          sx={{
                            color: THEME.primary,
                            opacity: 0,
                            transition: 'opacity 0.15s ease',
                            '.MuiListItemButton-root:hover &': { opacity: 1 }
                          }}
                        >
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  ))}

                  <Divider sx={{ my: 2, mx: 1 }} />
                </>
              )}

              {/* No users found */}
              {sortedUsers.length === 0 && searchQuery && activeSection !== 'conversations' && (
                <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
                  <Typography variant="body2" color={THEME.textSecondary}>
                    No people found for "{searchQuery}"
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ mt: 1, color: THEME.primary, textTransform: 'none' }}
                  >
                    Clear search
                  </Button>
                </Box>
              )}

              {/* Conversations Section */}
              {renderSectionHeader(
                'Conversations',
                filteredConversations.length,
                <ChatIcon fontSize="small" sx={{ color: THEME.secondary }} />,
                activeSection === 'conversations'
              )}

              {(activeSection === 'all' || activeSection === 'conversations') && (
                filteredConversations.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                    <Box sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      bgcolor: THEME.primaryLight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      animation: 'pulse 2s infinite'
                    }}>
                      <ChatIcon sx={{ color: THEME.primary, fontSize: 32 }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600} color={THEME.textPrimary}>
                      No conversations yet
                    </Typography>
                    <Typography variant="body2" color={THEME.textSecondary} sx={{ mt: 0.5, mb: 2 }}>
                      Start chatting with your team to see conversations here
                    </Typography>
                    <Chip
                      icon={<PersonAddIcon fontSize="small" />}
                      label="Find people to chat"
                      onClick={() => { setSearchQuery(''); setActiveSection('people'); }}
                      sx={{
                        bgcolor: THEME.primary,
                        color: 'white',
                        fontWeight: 500,
                        '&:hover': { bgcolor: '#5a6fd6' },
                        cursor: 'pointer'
                      }}
                    />
                  </Box>
                ) : (
                  filteredConversations.map((conv, index) => {
                    const other = conv.other_users?.[0];
                    const unread = getUnreadCount(conv);
                    const isOtherOnline = onlineUsersData?.some(
                      u =>
                        u.id === other?.id &&
                        (
                          u.is_online === true ||
                          u.status === 'online'
                        )
                    );
                    const isGroup = conv.type === 'group';

                    return (
                      <React.Fragment key={conv.id}>
                        <ListItemButton
                          onClick={() => handleConversationClick(conv.id)}
                          sx={{
                            py: 1.25,
                            px: 1.5,
                            borderRadius: 2,
                            my: 0.25,
                            bgcolor: unread > 0 ? THEME.hoverPrimary : 'transparent',
                            borderLeft: unread > 0 ? `3px solid ${THEME.primary}` : '3px solid transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: unread > 0 ? THEME.hoverPrimary : THEME.hover,
                              transform: 'translateX(3px)'
                            }
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 56 }}>
                            <Box sx={{ position: 'relative' }}>
                              <Avatar
                                src={getAvatarUrl(other?.avatar)}
                                sx={{
                                  width: 44,
                                  height: 44,
                                  bgcolor: isGroup ? THEME.secondary : THEME.primary,
                                  fontSize: '1.1rem',
                                  border: '2px solid white',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }}
                              >
                                {isGroup ? <GroupAddIcon /> : other?.name?.charAt(0)?.toUpperCase()}
                              </Avatar>
                              {!isGroup && isOtherOnline && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 2,
                                    right: 2,
                                    width: 11,
                                    height: 11,
                                    bgcolor: THEME.online,
                                    borderRadius: '50%',
                                    border: '2px solid white'
                                  }}
                                />
                              )}
                            </Box>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={unread > 0 ? 700 : 600}
                                  color={unread > 0 ? THEME.textPrimary : THEME.textSecondary}
                                  noWrap
                                  sx={{ maxWidth: '70%' }}
                                >
                                  {isGroup ? conv.name : other?.name}
                                </Typography>
                                {conv.last_message?.created_at && (
                                  <Typography variant="caption" color={THEME.textDisabled} sx={{ flexShrink: 0, ml: 1 }}>
                                    {formatLastSeen(conv.last_message.created_at)}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {unread > 0 && (
                                  <Box sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: THEME.unreadDot,
                                    flexShrink: 0
                                  }} />
                                )}
                                <Typography
                                  variant="body2"
                                  color={unread > 0 ? THEME.textPrimary : THEME.textSecondary}
                                  sx={{
                                    lineHeight: 1.3,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    flex: 1,
                                    fontWeight: unread > 0 ? 500 : 400
                                  }}
                                >
                                  {conv.last_message?.file_name
                                    ? `📎 ${conv.last_message.file_name}`
                                    : truncateText(conv.last_message?.body || 'No messages yet')}
                                </Typography>
                                {conv.last_message?.sender_id === authUser?.id && (
                                  <CheckIcon sx={{ fontSize: 16, color: THEME.textDisabled, flexShrink: 0 }} />
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                          />

                          {unread > 0 && (
                            <Badge
                              badgeContent={unread}
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  right: -8,
                                  top: 8,
                                  fontSize: '0.7rem',
                                  minWidth: 18,
                                  height: 18
                                }
                              }}
                            />
                          )}
                        </ListItemButton>

                        {index < filteredConversations.length - 1 && (
                          <Divider sx={{ mx: 2, opacity: 0.4 }} component="li" />
                        )}
                      </React.Fragment>
                    );
                  })
                )
              )}
            </>
          )}
        </List>

        {/* Footer */}
        <Box sx={{
          p: 2,
          borderTop: `1px solid ${THEME.borderLight}`,
          bgcolor: THEME.surface,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Button
            variant="text"
            size="small"
            onClick={() => { }}
            startIcon={<PersonAddIcon fontSize="small" />}
            sx={{
              color: THEME.primary,
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { bgcolor: THEME.primaryLight }
            }}
          >
            Start new conversation
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChatDrawer;